import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import type { CampaignOfferPayload } from './campaignOfferCopy';
import {
  CampaignBonusOffersContext,
  EMPTY_CAMPAIGN_OFFERS,
  type CampaignBonusOffersValue,
} from './CampaignBonusOffersContext';

export function CampaignBonusOffersProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [payload, setPayload] = useState<CampaignOfferPayload>(EMPTY_CAMPAIGN_OFFERS);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!apiService.isAuthenticated()) {
        if (!cancelled) setPayload(EMPTY_CAMPAIGN_OFFERS);
        return;
      }
      try {
        const response = await apiService.get<CampaignOfferPayload>(
          '/payment/campaign-bonus-offers',
          true,
          true
        );
        if (cancelled) return;
        const data = response.success ? response.data : null;
        setPayload(data && Array.isArray(data.offers) ? data : EMPTY_CAMPAIGN_OFFERS);
      } catch {
        if (!cancelled) setPayload(EMPTY_CAMPAIGN_OFFERS);
      }
    };
    void load();
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, load);
    };
  }, [location.pathname]);

  const dismissPopup = useCallback(async () => {
    const response = await apiService.post<CampaignOfferPayload>(
      '/payment/campaign-bonus-offers/dismiss',
      {},
      true
    );
    if (response.success && response.data && Array.isArray(response.data.offers)) {
      setPayload({ ...response.data, popupDismissed: true });
    }
  }, []);

  const value = useMemo<CampaignBonusOffersValue>(
    () => ({ ...payload, dismissPopup }),
    [payload, dismissPopup]
  );

  return (
    <CampaignBonusOffersContext.Provider value={value}>
      {children}
    </CampaignBonusOffersContext.Provider>
  );
}
